import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Home,
  Bathtub,
  KingBed,
  SquareFoot,
  AttachMoney,
  CheckCircle,
  LocationOn,
  Apartment,
  Edit,
  Print,
  Share,
  Phone,
  Email,
  ArrowBack
} from '@mui/icons-material';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useGetUnitByIdQuery } from './api/unitApi';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'transform 0.3s',
  '&:hover': {
    transform: 'translateY(-2px)'
  }
}));

const PriceDisplay = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius * 2,
  marginBottom: theme.spacing(3)
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius
}));

const UnitDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data, error, isLoading } = useGetUnitByIdQuery(id!);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">Error loading unit details: {error.toString()}</Alert>
      </Box>
    );
  }

  if (!data?.data) {
    return (
      <Box mt={4}>
        <Alert severity="warning">Unit not found</Alert>
      </Box>
    );
  }

  const unit = data.data;
  const images = unit.images?.map((img: any) => img.url) || [];
  if (unit.unit_image) images.unshift(unit.unit_image);

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 }, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Header with Breadcrumbs */}
      <Box mb={4}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link color="inherit" href="/units" underline="hover">
            <Stack direction="row" alignItems="center" spacing={1}>
              <ArrowBack fontSize="small" />
              <Typography>All Units</Typography>
            </Stack>
          </Link>
          <Typography color="text.primary">{unit.unit_code}</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              {unit.unit_type?.name || 'Luxury Unit'} - {unit.unit_code}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {unit.project?.title}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={4}>
        {/* Main Image Carousel and Project Details */}
        <Grid item xs={12} md={7}>
          <Stack spacing={4}>
            {/* Gallery */}
            <StyledCard>
              {images.length > 0 ? (
                <Carousel
                  showThumbs={images.length > 1}
                  showStatus={false}
                  infiniteLoop
                  stopOnHover
                >
                  {images.map((img: any, index: any) => (
                    <Box key={index} sx={{ height: { xs: 300, md: 500 } }}>
                      <img
                        src={img}
                        alt={`Unit image ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '16px 16px 0 0'
                        }}
                      />
                    </Box>
                  ))}
                </Carousel>
              ) : (
                <Box
                  sx={{
                    height: { xs: 300, md: 500 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.100'
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No images available
                  </Typography>
                </Box>
              )}
            </StyledCard>

            {/* Project Details */}
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Project Details
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <LocationOn color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={`Lat: ${unit.project?.latitude}, Lng: ${unit.project?.longitude}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Home color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Amenities"
                      secondary={
                        <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {unit.project?.amenities?.map((amenity:any, i:any) => (
                            <Chip key={i} label={amenity} size="small" />
                          ))}
                        </Box>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </StyledCard>
          </Stack>
        </Grid>

        {/* Details Section */}
        <Grid item xs={12} md={5}>
          <Stack spacing={4}>
            {/* Price Section */}
            <StyledCard>
              <CardContent>
                <PriceDisplay elevation={0}>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Price Range
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${Number(unit.price_min).toLocaleString()} - ${Number(unit.price_max).toLocaleString()}
                    </Typography>
                  </Stack>
                  <Chip
                    label={unit.availability}
                    color={unit.availability === 'Available' ? 'success' : 'default'}
                    size="medium"
                    icon={<CheckCircle />}
                  />
                </PriceDisplay>

                <Divider sx={{ my: 3 }} />

                {/* Key Features */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <KingBed color="primary" />
                      <Typography>
                        <strong>{unit.bedrooms}</strong> Bedrooms
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Bathtub color="primary" />
                      <Typography>
                        <strong>{unit.bathrooms}</strong> Bathrooms
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <SquareFoot color="primary" />
                      <Typography>
                        <strong>{unit.size_sqm}</strong> sqm
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Apartment color="primary" />
                      <Typography>
                        {unit.floor_level ? `Floor ${unit.floor_level}` : 'Single Level'}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Special Features */}
            {unit.special_features?.length > 0 && (
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Special Features
                  </Typography>
                  <Box>
                    {unit.special_features.map((feature: any, index: any) => (
                      <FeatureChip
                        key={index}
                        label={feature}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </CardContent>
              </StyledCard>
            )}

            {/* Unit Description */}
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Unit Description
                </Typography>
                <Typography paragraph>
                  This {unit.unit_type?.name} unit features {unit.bedrooms} bedrooms and {unit.bathrooms} bathrooms
                  across {unit.size_sqm} square meters of living space.
                </Typography>
                <Typography paragraph>
                  {unit.description}
                </Typography>
              </CardContent>
            </StyledCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UnitDetailsPage;